'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { SessionProvider, useSession } from 'next-auth/react'
import { useEffect, useState } from 'react'
import { getAnalytics } from '@/lib/analytics/client'

/** Identifies the logged-in account (accountId only — no PII). Must be a CHILD of SessionProvider. */
function AnalyticsIdentify() {
  const { data: session, status } = useSession()
  const accountId = session?.user?.activeAccountId
  useEffect(() => {
    if (status === 'authenticated' && accountId) getAnalytics().identify(accountId)
    else if (status === 'unauthenticated') getAnalytics().reset()
  }, [status, accountId])
  return null
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh longer
            gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache
            refetchOnWindowFocus: false, // Don't refetch on window focus
            refetchOnReconnect: true, // Refetch on reconnect
            retry: 1, // Only retry once on failure
          },
        },
      })
  )

  return (
    <SessionProvider>
      <AnalyticsIdentify />
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </SessionProvider>
  )
}
